import 'src/styles/ui/notice-card.css';

import { faCheck, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { POWDER_QUALITY_BADGE_DATA } from 'src/components/BatchPowderQualityStatusIcon';
import BlendAttributeValue from 'src/components/blend-check/BlendAttributeValue';
import BlendCardSide from 'src/components/blend-check/BlendCardSide';
import { BLEND_ATTRIBUTE, getPowderQualityLabel } from 'src/utils/blendAttributes';
import { pluralWord } from 'src/utils/stringUtils';

/** "Becomes": the summarised result, never a reprint of the ids above it. */
const getResultText = attribute => {
  const { becomes, key } = attribute;

  switch (key) {
    case BLEND_ATTRIBUTE.MATERIAL_TYPE:
      return `${becomes.count} ${pluralWord('type', becomes.count)}`;
    case BLEND_ATTRIBUTE.MATERIAL_LOT:
      return `${becomes.count} ${pluralWord('lot', becomes.count)}`;
    case BLEND_ATTRIBUTE.USAGE_CYCLES:
      return `${becomes.value} ${pluralWord('cycle', becomes.value)}`;
    case BLEND_ATTRIBUTE.POWDER_QUALITY:
      return getPowderQualityLabel(becomes.status);
    default:
      return null;
  }
};

const ResultFooter = ({ attribute }) => {
  const { becomes } = attribute;
  // Powder quality carries its own meaning, independent of whether the value
  // moved: the app treats Unsieved and Unknown as caution states and New and
  // Sieved as fine. Colouring this tile by "did it change" instead would put an
  // amber Sieved directly under two green ones - and leave an already-Unsieved
  // destination looking like a success.
  const changed =
    attribute.key === BLEND_ATTRIBUTE.POWDER_QUALITY
      ? Boolean(becomes.isCaution)
      : becomes.changesDestination;

  // For powder quality show the status the batch actually lands in, rather than
  // a generic warning mark.
  const qualityBadge = becomes.status ? POWDER_QUALITY_BADGE_DATA[becomes.status] : null;
  const icon = qualityBadge?.icon || (changed ? faCircleExclamation : faCheck);

  return (
    <div
      className={classNames('blend-compare__result', { 'blend-compare__result--changed': changed })}
    >
      <span
        className={classNames(
          'notice-tile notice-tile--sm',
          changed ? 'notice-tile--warn' : 'notice-tile--ok'
        )}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
      <div className='blend-compare__result-main'>
        <div className='blend-compare__result-caption'>Becomes</div>
        <div className='blend-compare__result-value'>{getResultText(attribute)}</div>
        <div className='blend-compare__result-reason'>{becomes.reason}</div>
      </div>
    </div>
  );
};

/**
 * One card per comparison: the two sides are named once in a shared header,
 * Source left and Destination right for every attribute, result as the footer.
 * `single` renders the one-panel variant used when a batch is itself a blend.
 */
const BlendComparisonCard = ({ attribute, source, destination, lotsByUri, single }) => {
  const uniqueLotUris = useMemo(() => {
    if (attribute.key !== BLEND_ATTRIBUTE.MATERIAL_LOT || single) {
      return new Set();
    }
    const sourceLots = new Set(attribute.source || []);
    return new Set((attribute.destination || []).filter(lotUri => !sourceLots.has(lotUri)));
  }, [attribute, single]);

  const renderValue = (value, marked) => (
    <BlendAttributeValue
      attributeKey={attribute.key}
      value={value}
      lotsByUri={lotsByUri}
      uniqueLotUris={marked ? uniqueLotUris : new Set()}
    />
  );

  return (
    <div className='blend-compare'>
      <div className={classNames('blend-compare__head', { 'blend-compare__head--single': single })}>
        <BlendCardSide
          single={single}
          variant='source'
          caption={single ? 'In Batch' : 'Source'}
          resource={source}
        />
        {!single && (
          <BlendCardSide variant='destination' caption='Destination' resource={destination} />
        )}
      </div>

      {single ? (
        <div className='blend-compare__col blend-compare__col--single'>
          {renderValue(attribute.source, false)}
        </div>
      ) : (
        <div className='blend-compare__split'>
          <div className='blend-compare__col'>{renderValue(attribute.source, false)}</div>
          <div className='blend-compare__col'>{renderValue(attribute.destination, true)}</div>
        </div>
      )}

      {attribute.becomes && <ResultFooter attribute={attribute} />}
    </div>
  );
};

ResultFooter.propTypes = {
  attribute: PropTypes.shape({
    key: PropTypes.string.isRequired,
    becomes: PropTypes.object.isRequired,
  }).isRequired,
};

BlendComparisonCard.propTypes = {
  attribute: PropTypes.shape({
    key: PropTypes.string.isRequired,
    source: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
    destination: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
    becomes: PropTypes.object,
  }).isRequired,
  source: PropTypes.object,
  destination: PropTypes.object,
  lotsByUri: PropTypes.object,
  single: PropTypes.bool,
};

BlendComparisonCard.defaultProps = {
  source: null,
  destination: null,
  lotsByUri: {},
  single: false,
};

export default BlendComparisonCard;
